import React, { useState } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { toast } from "react-toastify";

const GAMES = [
  { key: "gta-6", label: "GTA VI" },
  { key: "gta-5", label: "GTA V" },
  { key: "gta-4", label: "GTA IV" },
  { key: "sa", label: "GTA: San Andreas" },
  { key: "vc", label: "GTA: Vice City" },
  { key: "gta-3", label: "GTA III" },
  { key: "other", label: "Other" },
];

const labelByKey = (k) => GAMES.find((g) => g.key === k)?.label || "Other";

export default function CharacterStoryForm({
  user,
  authUser, // optional; falls back to `user`
  role,
  onDone,
  onCancel,
  showList = false,
}) {
  const currentUser = authUser ?? user;
  const [submitting, setSubmitting] = useState(false);
  const [banner, setBanner] = useState({ type: null, text: "" });
  const [issues, setIssues] = useState([]);

  const [form, setForm] = useState({
    characterName: "",
    gameKey: "gta-6",
    game: "GTA VI",
    tags: "",
    synopsis: "",
    story: "",
    imageFile: null,
    imageUrl: "",
    isPrivate: true,
    // NEW: Bio fields
    fullName: "",
    born: "", // e.g., "1988, Los Santos"
    background: "",
    personality: "",
    skills: "",
    dreams: "",
    liking: "", // “likes/liking” (hobbies/interests)
    partner: "", // Girlfriend/Wife (name or status)
    children: "", // e.g., "None" or names/count
  });

  const fillDemoCharacter = () => {
    setForm({
      characterName: "Devin Weston",
      gameKey: "gta-5",
      game: "GTA V",
      tags: "antagonist, gta5, billionaire, merryweather",
      synopsis:
        "Devin Weston, a ruthless billionaire investor, uses his wealth and power to manipulate others, leaving chaos in his wake.",
      story:
        "Devin Weston is a venture capitalist and corporate mogul who thrives on corruption, greed, and control. Using his investments in Merryweather and government influence, he exploits Michael, Franklin, and Trevor for his personal gain. His arrogance and disregard for loyalty make him one of the most despised figures in Los Santos.",
      imageFile: null,
      imageUrl: "",
      isPrivate: true,
      fullName: "Devin Weston",
      born: "1960s, United States (exact date unknown)",
      background:
        "A self-made billionaire, Weston built his empire through ruthless corporate tactics and shady political connections. He owns major stakes in numerous businesses, including Merryweather Security.",
      personality:
        "Arrogant, manipulative, self-serving, and smug. He believes wealth makes him untouchable.",
      skills:
        "Business manipulation, political influence, private security access, corporate strategy.",
      dreams:
        "To expand his wealth and power without consequences, regardless of who suffers.",
      liking: "Luxury cars, exploiting others, high-end lifestyle, dominance.",
      partner: "None (uses people only as pawns).",
      children: "None (unconfirmed).",
    });
  };

  const storage = getStorage();

  const onChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "imageFile") {
      setForm((f) => ({ ...f, imageFile: files?.[0] || null }));
      return;
    }

    if (name === "gameKey") {
      setForm((f) => ({
        ...f,
        gameKey: value,
        game: labelByKey(value),
      }));
      return;
    }

    setForm((f) => ({ ...f, [name]: value }));
  };

  const resetForm = () => {
    setForm({
      characterName: "",
      gameKey: "gta-6",
      game: "GTA VI",
      tags: "",
      synopsis: "",
      story: "",
      imageFile: null,
      imageUrl: "",

      // NEW: reset bio
      fullName: "",
      born: "",
      background: "",
      personality: "",
      skills: "",
      dreams: "",
      liking: "",
      partner: "",
      children: "",
    });
    setIssues([]);
  };

  const uploadImageIfAny = async () => {
    if (!form.imageFile || !currentUser) return form.imageUrl || "";
    const file = form.imageFile;
    const path = `characters/${currentUser.uid}/${Date.now()}_${file.name}`;
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    return url;
  };

  const validate = () => {
    const errs = [];
    if (!currentUser?.uid) errs.push("You must be signed in.");
    if (!form.characterName.trim()) errs.push("Character name is required.");
    if (!form.game.trim()) errs.push("Game is required.");
    if (!form.story.trim()) errs.push("Full story is required.");

    // NEW: validate Bio’s Full Name
    if (!form.fullName.trim()) errs.push("Bio: Full Name is required.");
    return errs;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const errs = validate();
    setIssues(errs);
    if (errs.length > 0) return;

    setSubmitting(true);
    setBanner({ type: null, text: "" });

    try {
      const imageUrl = await uploadImageIfAny();

      // NEW: build bio object
      const bio = {
        fullName: form.fullName.trim(),
        born: form.born.trim(),
        background: form.background.trim(),
        personality: form.personality.trim(),
        skills: form.skills.trim(),
        dreams: form.dreams.trim(),
        liking: form.liking.trim(),
        partner: form.partner.trim(),
        children: form.children.trim(),
      };

      const payload = {
        characterName: form.characterName.trim(),
        game: form.game.trim(), // display label
        gameKey: form.gameKey, // canonical key
        tags: form.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        synopsis: form.synopsis.trim(),
        story: form.story.trim(),
        imageUrl: imageUrl || "",
        bio, // NEW
        isPrivate: !!form.isPrivate,
        authorUid: currentUser.uid,
        roleAtCreation: role || "member",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await addDoc(collection(db, "characters"), payload);

      setBanner({ type: "ok", text: "Entry published successfully." });
      toast.success("Character saved successfully"); // ✅ toast on success
      resetForm();
      onDone?.();
    } catch (err) {
      console.error(err);
      setBanner({ type: "error", text: "Submit failed. Try again." });
      toast.error("Submit failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const previewTags = form.tags
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);

  return (
    <>
      {banner.type && (
        <div
          className={
            "mt6-banner " +
            (banner.type === "ok"
              ? "mt6-banner--ok"
              : banner.type === "warn"
              ? "mt6-banner--warn"
              : "mt6-banner--error")
          }
        >
          {banner.text}
        </div>
      )}

      {issues.length > 0 && (
        <ul className="mt6-issues" role="alert">
          {issues.map((m, i) => (
            <li key={i}>• {m}</li>
          ))}
        </ul>
      )}

      <form onSubmit={onSubmit} className="mt6-form">
        {/* ---- Basics ---- */}
        <div className="mt6-section">
  <h4 className="mt6-h4">Basics</h4>

  <div className="mt6-row-3">
    {/* Character Name */}
    <div>
      <label className="mt6-label">Character Name*</label>
      <input
        className="mt6-input"
        name="characterName"
        value={form.characterName}
        onChange={onChange}
        required
        placeholder="e.g., Lucia"
        disabled={submitting}
      />
    </div>

    {/* Game */}
    <div>
      <label className="mt6-label">Game*</label>
      <select
        className="mt6-input"
        name="gameKey"
        value={form.gameKey}
        onChange={onChange}
        required
        disabled={submitting}
      >
        {GAMES.map(({ key, label }) => (
          <option key={key} value={key}>
            {label}
          </option>
        ))}
      </select>
    </div>

    {/* Visibility */}
    <div>
      <label className="mt6-label">Visibility</label>
      <div className="mt6-segment" role="tablist" aria-label="Visibility">
        <button
          type="button"
          className={`mt6-seg-btn ${!form.isPrivate ? "is-active" : ""}`}
          onClick={() => setForm((f) => ({ ...f, isPrivate: false }))}
          aria-pressed={!form.isPrivate}
          disabled={submitting}
        >
          Public
        </button>
        <button
          type="button"
          className={`mt6-seg-btn ${form.isPrivate ? "is-active" : ""}`}
          onClick={() => setForm((f) => ({ ...f, isPrivate: true }))}
          aria-pressed={form.isPrivate}
          disabled={submitting}
        >
          Private
        </button>
      </div>

      <div className="mt6-muted" aria-live="polite" style={{ marginTop: 6 }}>
        {form.isPrivate
          ? "Private: visible only to you."
          : "Public: visible to everyone."}
      </div>
    </div>
  </div>
</div>


        {/* ---- Bio (NEW) ---- */}
        <div className="mt6-section">
          <h4 className="mt6-h4">Bio</h4>

          <div className="mt6-row-3">
            <div>
              <label className="mt6-label">Full Name*</label>
              <input
                className="mt6-input"
                name="fullName"
                value={form.fullName}
                onChange={onChange}
                required
                placeholder="e.g., Franklin Clinton"
                disabled={submitting}
              />
            </div>

            <div>
              <label className="mt6-label">Born</label>
              <input
                className="mt6-input"
                name="born"
                value={form.born}
                onChange={onChange}
                placeholder="e.g., 1988, Los Santos"
                disabled={submitting}
              />
            </div>

            <div>
              <label className="mt6-label">Liking</label>
              <input
                className="mt6-input"
                name="liking"
                value={form.liking}
                onChange={onChange}
                placeholder="e.g., cars, hustle, strategy"
                disabled={submitting}
              />
            </div>
          </div>

          <div className="mt6-row-3">
            <div>
              <label className="mt6-label">Girlfriend/Wife</label>
              <input
                className="mt6-input"
                name="partner"
                value={form.partner}
                onChange={onChange}
                placeholder="e.g., Tanisha"
                disabled={submitting}
              />
            </div>

            <div>
              <label className="mt6-label">Children</label>
              <input
                className="mt6-input"
                name="children"
                value={form.children}
                onChange={onChange}
                placeholder="e.g., None"
                disabled={submitting}
              />
            </div>

            <div />
          </div>

          <label className="mt6-label">Background</label>
          <textarea
            className="mt6-input mt6-input--textarea"
            name="background"
            value={form.background}
            onChange={onChange}
            rows={3}
            placeholder="Grew up in Los Santos... raised by grandmother..."
            disabled={submitting}
          />

          <label className="mt6-label">Personality</label>
          <textarea
            className="mt6-input mt6-input--textarea"
            name="personality"
            value={form.personality}
            onChange={onChange}
            rows={3}
            placeholder="Ambitious, street-smart, practical..."
            disabled={submitting}
          />

          <label className="mt6-label">Skills</label>
          <textarea
            className="mt6-input mt6-input--textarea"
            name="skills"
            value={form.skills}
            onChange={onChange}
            rows={3}
            placeholder="Excellent driver (slow-mo control), firearms, tactics..."
            disabled={submitting}
          />

          <label className="mt6-label">Dreams</label>
          <textarea
            className="mt6-input mt6-input--textarea"
            name="dreams"
            value={form.dreams}
            onChange={onChange}
            rows={3}
            placeholder="Rise above gang life and become a “big player” in Los Santos..."
            disabled={submitting}
          />
        </div>

        {/* ---- Metadata ---- */}
        <div className="mt6-section">
          <h4 className="mt6-h4">Metadata</h4>

          <label className="mt6-label">Tags (CSV)</label>
          <input
            className="mt6-input"
            name="tags"
            value={form.tags}
            onChange={onChange}
            placeholder="backstory, fan-fiction, vice-city"
            disabled={submitting}
          />

          {previewTags.length > 0 && (
            <div className="mt6-pills" style={{ marginTop: 8 }}>
              {previewTags.map((t) => (
                <span key={t} className="mt6-pill is-active">
                  #{t}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* ---- Content ---- */}
        <div className="mt6-section">
          <h4 className="mt6-h4">Content</h4>

          <label className="mt6-label">Short Synopsis</label>
          <textarea
            className="mt6-input mt6-input--textarea"
            name="synopsis"
            value={form.synopsis}
            onChange={onChange}
            rows={3}
            placeholder="One-line hook for your story…"
            disabled={submitting}
          />

          <label className="mt6-label">Full Story*</label>
          <textarea
            className="mt6-input mt6-input--textarea"
            name="story"
            value={form.story}
            onChange={onChange}
            rows={10}
            required
            placeholder="Write your story here…"
            disabled={submitting}
          />
        </div>

        {/* ---- Media ---- */}
        <div className="mt6-section">
          <h4 className="mt6-h4">Media</h4>

          <div className="mt6-row-3">
            <div>
              <label className="mt6-label">Cover Image</label>
              <input
                type="file"
                name="imageFile"
                accept="image/*"
                onChange={onChange}
                className="mt6-input"
                disabled={submitting}
              />
            </div>

            {form.imageUrl ? (
              <div style={{ gridColumn: "span 2" }}>
                <label className="mt6-label">Existing Image</label>
                <div className="mt6-preview">
                  <img
                    src={form.imageUrl}
                    alt="Cover image preview"
                    style={{ maxWidth: 240, borderRadius: 10 }}
                  />
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {/* ---- Preview ---- */}
        <div className="mt6-section">
          <h4 className="mt6-h4">Preview</h4>
          <div className="mt6-preview">
            <div className="mt6-muted">Character</div>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>
              {form.characterName || "—"} <span>• {form.game}</span>
                <span style={{ marginLeft: 8, fontWeight: 400 }}>
                  {form.isPrivate ? "• Private" : "• Public"}
                </span>
            </div>

            {/* NEW: Bio preview */}
            {(form.fullName ||
              form.born ||
              form.background ||
              form.personality ||
              form.skills ||
              form.dreams ||
              form.liking ||
              form.partner ||
              form.children) && (
              <div style={{ marginTop: 10 }}>
                <div className="mt6-muted">Bio</div>
                {form.fullName && (
                  <div>
                    <strong>Full Name:</strong> {form.fullName}
                  </div>
                )}
                {form.born && (
                  <div>
                    <strong>Born:</strong> {form.born}
                  </div>
                )}
                {form.background && (
                  <div>
                    <strong>Background:</strong> {form.background}
                  </div>
                )}
                {form.personality && (
                  <div>
                    <strong>Personality:</strong> {form.personality}
                  </div>
                )}
                {form.skills && (
                  <div>
                    <strong>Skills:</strong> {form.skills}
                  </div>
                )}
                {form.dreams && (
                  <div>
                    <strong>Dreams:</strong> {form.dreams}
                  </div>
                )}
                {(form.liking || form.partner || form.children) && (
                  <div style={{ marginTop: 6 }}>
                    {form.liking && (
                      <div>
                        <strong>Liking:</strong> {form.liking}
                      </div>
                    )}
                    {form.partner && (
                      <div>
                        <strong>Girlfriend/Wife:</strong> {form.partner}
                      </div>
                    )}
                    {form.children && (
                      <div>
                        <strong>Children:</strong> {form.children}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {form.synopsis && (
              <>
                <div className="mt6-muted" style={{ marginTop: 10 }}>
                  Synopsis
                </div>
                <div style={{ marginBottom: 6 }}>{form.synopsis}</div>
              </>
            )}

            {previewTags.length > 0 && (
              <div className="mt6-pills" style={{ marginTop: 8 }}>
                {previewTags.map((t) => (
                  <span key={t} className="mt6-pill is-active">
                    #{t}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ---- Actions ---- */}
        <div className="mt6-actions">
          {/* <button
            type="button"
            className="mt6-btn mt6-btn--ghost"
            onClick={fillDemoCharacter}
            disabled={submitting}
          >
            Fill Demo Character
          </button> */}

          <button
            className="mt6-btn mt6-btn--primary"
            type="submit"
            disabled={submitting}
            aria-busy={submitting}
          >
            {submitting ? "Submitting..." : "Submit"}
          </button>

          {onCancel && (
            <button
              type="button"
              className="mt6-btn mt6-btn--ghost"
              onClick={onCancel}
              disabled={submitting}
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </>
  );
}
